'use client'

import * as React from 'react'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { FadeIn } from '../motion/FadeIn'

interface TermsSection {
  heading: string
  content: string | React.ReactNode
}

interface TermsPageProps {
  heading?: string
  lastUpdated: string
  appName: string
  email: string
  sections?: TermsSection[]
  companyInfo?: {
    name: string
    location: string
    registration?: string
  }
}

const defaultSections = (appName: string): TermsSection[] => [
  {
    heading: '1. Acceptance of Terms',
    content: `By accessing or using ${appName} ("the Service"), you agree to be bound by these Terms of Service. The Service is operated by Van Moose, based in Amsterdam, Netherlands.`,
  },
  {
    heading: '2. Description of Service',
    content: `${appName} provides tools and services as described on our website. The Service includes both free and paid tiers with varying features and usage limits.`,
  },
  {
    heading: '3. Acceptable Use',
    content: (
      <>
        <p className="mb-4">You agree NOT to use the Service to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe on intellectual property rights of others</li>
          <li>Attempt to circumvent rate limits or access controls</li>
          <li>Resell or redistribute the Service without authorization</li>
          <li>Use automated systems to abuse the Service</li>
        </ul>
      </>
    ),
  },
  {
    heading: '4. Intellectual Property',
    content: `${appName} and its original content, features, and functionality are owned by Van Moose. Users retain ownership of their own content but grant us a license to use it for providing the Service.`,
  },
  {
    heading: '5. Paid Subscriptions',
    content: (
      <>
        <p className="mb-4">
          Paid subscriptions are billed monthly or annually. All payments are processed securely through Stripe.
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Subscriptions renew automatically unless cancelled</li>
          <li>Refunds are available within 14 days of purchase</li>
          <li>We reserve the right to modify pricing with 30 days notice</li>
        </ul>
      </>
    ),
  },
  {
    heading: '6. API Usage',
    content: 'API access is subject to rate limits based on your subscription tier. Exceeding rate limits may result in temporary suspension. Sharing API keys is prohibited and may result in account termination.',
  },
  {
    heading: '7. Disclaimer of Warranties',
    content: 'The Service is provided "as is" without warranties of any kind. We do not guarantee uninterrupted access or that the Service will meet your specific requirements.',
  },
  {
    heading: '8. Limitation of Liability',
    content: 'Van Moose shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service. Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.',
  },
  {
    heading: '9. Termination',
    content: 'We may terminate or suspend your access to the Service at any time for violation of these terms. You may cancel your account at any time through the dashboard settings.',
  },
  {
    heading: '10. Governing Law',
    content: 'These terms are governed by the laws of the Netherlands. Any disputes shall be resolved in the courts of Amsterdam, Netherlands.',
  },
]

export function TermsPage({
  heading = 'Terms of Service',
  lastUpdated,
  appName,
  email,
  sections,
  companyInfo = {
    name: 'Van Moose',
    location: 'Amsterdam, Netherlands',
  },
}: TermsPageProps) {
  const contentSections = sections || defaultSections(appName)

  return (
    <Section className="min-h-[60vh]">
      <Container size="md">
        <FadeIn>
          <h1 className="headline-lg mb-2">{heading}</h1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="text-[var(--landing-text-tertiary)] mb-12">
            Last updated: {lastUpdated}
          </p>
        </FadeIn>

        <div className="prose prose-lg text-[var(--landing-text-secondary)] space-y-8">
          {contentSections.map((section, index) => (
            <FadeIn key={section.heading} delay={0.15 + index * 0.05}>
              <section>
                <h2 className="text-2xl font-semibold text-[var(--landing-text-primary)] mb-4">
                  {section.heading}
                </h2>
                {typeof section.content === 'string' ? (
                  <p>{section.content}</p>
                ) : (
                  section.content
                )}
              </section>
            </FadeIn>
          ))}

          <FadeIn delay={0.6}>
            <section>
              <h2 className="text-2xl font-semibold text-[var(--landing-text-primary)] mb-4">
                11. Contact
              </h2>
              <p>
                Questions about these terms? Contact us at{' '}
                <a href={`mailto:${email}`} className="text-[var(--accent)] hover:underline">
                  {email}
                </a>
              </p>
              <p className="mt-4 text-sm text-[var(--landing-text-tertiary)]">
                {companyInfo.name}<br />
                {companyInfo.location}
                {companyInfo.registration && <><br />{companyInfo.registration}</>}
              </p>
            </section>
          </FadeIn>
        </div>
      </Container>
    </Section>
  )
}
